import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useTerminalsStateRE } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import {
  CardFilterTimeFrameIso,
  getTimeFrame,
} from "@/components/pageCards/filterSort/filterTimeFrame";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { useAccessParents } from "@/pages/infrastructure/functions";

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";

import { WasteTypesInUserTable } from "./WasteTypesInUserTable";
import { getWastetypesInUse, getWastetypesNotUsed } from "./functions";

export enum TerminalCategoryOptionsEnum {
  Office = "officebuilding",
  ShoppingCenter = "shoppingcenter",
}

interface WasteTypesInUseProps {
  timeFrame: CardFilterTimeFrameIso;
}

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const WasteTypesInUseCard: React.FC<Props> = ({
  timeFilter,
  setTimeFilters,
}) => {
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as any);
  const { terminalConfig } = useConfigService();

  const defaultPeriod = useMemo(
    () =>
      timeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last30Days,
    [terminalConfig, timeFilter],
  );

  const filters: FilterSort[] = [
    createTimeFilters({ defaultValue: defaultPeriod }),
  ];

  const timeFrame = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.WasteTypesInUse,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  return (
    <Card
      title={"circularityLabels:wasteTypeCategoriesInUse"}
      overflow="auto"
      filters={filters}
      onFilterOptionChange={onFilterOptionChange}
      filterLocation={CardComponentFilterLocation.RIGHT}
    >
      <WasteTypesInUse timeFrame={timeFrame} />
    </Card>
  );
};

export const WasteTypesInUse: React.FC<WasteTypesInUseProps> = ({
  timeFrame,
}) => {
  const { currentTerminal } = useTerminalsStateRE();

  const { wasteTypes: sanityWasteTypes } = useCommonEntitiesStore();
  const { setWasteTypesInUseLoading } = useHomeLoadingState();
  const { config } = useConfig();

  const {
    data,
    isError: isDataError,
    isLoading: wasteTypesLoading,
  } = trpc.dwh.terminals.getById.useQuery(
    {
      ...timeFrame,
      terminalId: currentTerminal.id,
    },
    {
      queryKeyHashFn: () =>
        [
          "terminal",
          currentTerminal.id,
          timeFrame.startTimeISO,
          timeFrame.endTimeISO,
        ].toString(),
    },
  );

  const { manageAccessParentPageData: wasteRoomData, isLoading } =
    useAccessParents();

  const {
    data: aggregatedData,
    isError: isAggregatesError,
    isLoading: aggregatesLoading,
  } = trpc.dwh.terminals.getAggregates.useQuery(
    {
      ...timeFrame,
      commercialTerminalCategory:
        currentTerminal.commercialRealEstateCategory as TerminalCategoryOptionsEnum,
    },
    {
      queryKeyHashFn: () =>
        [
          "terminalAggregates",
          timeFrame.startTimeISO,
          timeFrame.endTimeISO,
          currentTerminal.commercialRealEstateCategory,
        ].toString(),
    },
  );

  useEffect(() => {
    setWasteTypesInUseLoading(
      isLoading || wasteTypesLoading || aggregatesLoading,
    );
  }, [
    setWasteTypesInUseLoading,
    isLoading,
    wasteTypesLoading,
    aggregatesLoading,
  ]);

  const wasteTypeClassificationSystem = useMemo(
    () =>
      currentTerminal?.fractionDiscriminator ||
      config.wasteTypeClassificationSystemId ||
      "",
    [currentTerminal, config],
  );

  const isError = isDataError || isAggregatesError;

  const wasteTypesInUse = useMemo(
    () =>
      sanityWasteTypes && data
        ? getWastetypesInUse(data.terminal, wasteRoomData, sanityWasteTypes)
        : [],
    [data, wasteRoomData, sanityWasteTypes],
  );

  const wasteTypesNotInUse = useMemo(
    () =>
      aggregatedData && data && sanityWasteTypes
        ? getWastetypesNotUsed(
            aggregatedData.terminalAggregates,
            data.terminal,
            wasteRoomData,
            sanityWasteTypes,
            wasteTypeClassificationSystem,
          )
        : [],
    [
      aggregatedData,
      data,
      wasteRoomData,
      sanityWasteTypes,
      wasteTypeClassificationSystem,
    ],
  );

  return isError ? (
    <EmptyView type="failToFetch" className="min-h-[300px]" />
  ) : isLoading || aggregatesLoading ? (
    <div className="flex h-full min-h-full items-center justify-center">
      <LoadingIndicator />
    </div>
  ) : (
    <WasteTypesInUserTable
      typesInUse={wasteTypesInUse}
      futurePotential={wasteTypesNotInUse}
    />
  );
};
